import { IonLabel, IonSelect, IonSelectOption, IonText } from '@ionic/react';
import {
  ControlProps,
  ControlState,
  JsonSchema7,
  and,
  computeLabel,
  isEnumControl,
  or,
  rankWith,
  schemaMatches,
  uiTypeIs,
} from '@jsonforms/core';
import { Control, withJsonFormsControlProps } from '@jsonforms/react';
import { isObject, merge, orderBy } from 'lodash';
import { getLocalizedText } from '../../../i18n/i18n.utils';
import { IonItem } from '../../ionic/IonItem';
import './ionic-renderers.css';

class IonicPickerControlInner extends Control<ControlProps, ControlState> {
  private hasFocused = false;
  render() {
    const {
      data,
      description,
      enabled,
      errors,
      label,
      uischema,
      visible,
      required,
      config,
    } = this.props;

    const { isMultiSelect, selectOptions } = this.getOptions();

    const appliedUiSchemaOptions = merge({}, config, uischema.options);

    const errorText =
      data && this.hasFocused === true ? (
        <IonText color="danger">{errors}</IonText>
      ) : null;

    const classList: string[] = ['item-container'];
    if (visible === false) {
      classList.push('ion-hide');
    }

    if (data && errors === '') {
      classList.push('item-has-value');
    }

    const computedLabel = computeLabel(
      typeof label === 'string' ? label : '',
      required ?? false,
      appliedUiSchemaOptions.hideRequiredAsterisk
    );

    let sortedOptions: { label: string; value: string }[] = [];
    if (selectOptions) {
      if (isObject(selectOptions[0])) {
        sortedOptions = selectOptions.map((o) => ({
          label: o.title,
          value: o.const,
        }));
      } else {
        sortedOptions = selectOptions.map((o) => ({ label: o, value: o }));
      }
      sortedOptions = orderBy(sortedOptions, ['label'], ['asc']);
    }

    if (required !== true) {
      // on optional picklists, we want to give the user the ability to
      // remove the previously saved option by selecting '~ none ~'
      const none = getLocalizedText('common:none')?.toLowerCase();
      sortedOptions = [
        {
          label: `~ ${none} ~`,
          value: '',
        },
        ...sortedOptions,
      ];
    }

    return (
      <IonItem lines={'full'} className={classList.join(' ')}>
        <IonSelect
          aria-label={computedLabel}
          labelPlacement="floating"
          interface={isMultiSelect ? undefined : 'popover'}
          value={data}
          multiple={isMultiSelect}
          disabled={!enabled}
          placeholder={description}
          interfaceOptions={{
            header: computedLabel,
          }}
          onIonChange={(e) => {
            if (e.detail.value !== undefined) {
              this.hasFocused = true;
              this.onChange(e.detail.value);
            }
          }}
          onIonFocus={() => {
            this.onFocus();
          }}
          onIonBlur={() => {
            this.onBlur();
          }}
        >
          <div slot="label">
            <IonLabel color="primary">
              {computedLabel} {errorText}
            </IonLabel>
          </div>
          {sortedOptions.map((option, index) => {
            return (
              <IonSelectOption key={index} value={option.value}>
                {option.label}
              </IonSelectOption>
            );
          })}
        </IonSelect>
      </IonItem>
    );
  }
  private onChange = (value: string | undefined) => {
    const { path, handleChange } = this.props;

    if (value) {
      const { selectOptions, isMultiSelect } = this.getOptions();
      if (isMultiSelect) {
        handleChange(path, value);
      } else {
        for (const val in selectOptions) {
          const thisValue = selectOptions[val];
          if (value === thisValue) {
            handleChange(path, value);
          } else if (value === thisValue.const) {
            handleChange(path, thisValue.const);
          }
        }
      }
    } else {
      handleChange(path, undefined);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getOptions = (): { isMultiSelect: boolean; selectOptions: any[] } => {
    const { schema } = this.props;

    let isMultiSelect = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let selectOptions: any[] = [];
    const items = (schema.items as JsonSchema7) ?? {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const multiOptions: any[] | undefined = items.enum ?? items.anyOf;
    if (multiOptions) {
      selectOptions = multiOptions;
      isMultiSelect = true;
    } else if (schema.enum) {
      selectOptions = schema.enum;
    } else if (schema.oneOf) {
      selectOptions = schema.oneOf;
    }
    return { isMultiSelect, selectOptions };
  };
}

const isMultiSelectControl = and(
  uiTypeIs('Control'),
  schemaMatches((schema) => {
    if (schema.type === 'array' || schema.oneOf || schema.anyOf) {
      return true;
    } else {
      return false;
    }
  })
);

const IonicPickerTester = rankWith(10, or(isEnumControl, isMultiSelectControl));
const IonicPickerControl = withJsonFormsControlProps(IonicPickerControlInner);
export { IonicPickerControl, IonicPickerTester };
